import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Download, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, LoadingState, ErrorState } from "@/components/common";
import { QuoteDocument } from "@/modules/quotes/QuoteDocument";
import { usePortal } from "@/pages/portal/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, downloadFile } from "@/lib/api";
import { t } from "@/i18n";

const brand = { background: "var(--brand)", color: "var(--brand-contrast)" };

export default function PortalQuote() {
  const { id } = useParams();
  const { garage, slug } = usePortal();
  const { me } = useAuth();
  const { data, error, loading, reload } = useFetch(`/portal/${slug}/quotes/${id}`);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  if (me === null) return <Navigate to={`/g/${slug}/login`} replace />;
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const decide = async (action) => {
    setBusy(true);
    try {
      await api.post(`/portal/${slug}/quotes/${data.id}/${action}`, action === "accept" ? { version: data.version, comment } : { comment });
      toast.success(action === "accept" ? t("quotes.acceptedToast") : t("quotes.declinedToast"));
      reload();
    } catch (e) {
      toast.error(errMsg(e));
      reload();
    } finally {
      setBusy(false);
    }
  };
  const open = data.status === "sent" && data.is_current;
  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="portal-quote">
      <Link to={data.job_id ? `/g/${slug}/jobs/${data.job_id}` : `/g/${slug}/account?tab=workshop`} className="inline-flex items-center gap-1 text-sm text-slate-600" data-testid="portal-quote-back"><ArrowLeft className="h-4 w-4" /> {t("workshop.back")}</Link>
      <div className="flex justify-end">
        <Button variant="outline" onClick={() => downloadFile(`/portal/${slug}/quotes/${data.id}/pdf`).catch((e) => toast.error(errMsg(e)))} data-testid="portal-quote-pdf"><Download className="mr-2 h-4 w-4" /> PDF</Button>
      </div>
      {!data.is_current && <p className="rounded-lg bg-slate-100 p-3 text-sm text-slate-700">{t("quotes.olderVersion")}</p>}
      <QuoteDocument quote={data} garage={garage} />
      {open && (
        <Card testId="portal-quote-decision">
          <p className="font-display text-lg font-bold">{t("quotes.yourDecision")}</p>
          <p className="mt-1 text-sm text-slate-600">{t("quotes.decisionHint", { v: data.version })}</p>
          <Textarea className="mt-4" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("quotes.commentPlaceholder")} data-testid="portal-quote-comment" />
          <div className="mt-4 flex flex-wrap gap-2">
            <Button disabled={busy} style={brand} onClick={() => decide("accept")} data-testid="portal-quote-accept"><Check className="mr-2 h-4 w-4" /> {t("quotes.accept")}</Button>
            <Button disabled={busy} variant="outline" onClick={() => decide("decline")} data-testid="portal-quote-decline"><X className="mr-2 h-4 w-4" /> {t("quotes.decline")}</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
